import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import 'styles/main.css';

import { animated, useSpring } from '@react-spring/web';
import update from 'immutability-helper';

import API from 'files/api.js';

import Article from 'views/Article.js';
import Articles from 'views/Articles.js';
import Contact from 'views/Contact.js';
import HomePage from 'views/HomePage.js';
import DeepLook from 'views/DeepLook.js';
import DocPage from 'views/DocPage.js';
import QuickLook from 'views/QuickLook.js';
import { startAnalytics, trackView } from 'files/analytics.js';
import { locationForRoute, routeFromLocation } from 'files/routes.js';
import Appearance from 'styles/Appearance.js';
import Content from 'managers/Content.js';
import { EndIndex } from 'structure/layers.js';
import Events from 'managers/Events.js';
import Request from 'files/Request.js';
import Sheet from 'views/Sheet.js';
import { isMobile } from 'files/plain.js';

/* Both of these replace moment-timezone, which was imported here for exactly
   two things: a unix timestamp and a timezone name. The library carries the
   whole IANA database to answer the second, and the platform has answered both
   natively for years. It is still used by the legacy views, which are now in
   their own chunk, so this only takes it out of the bundle every visitor gets. */
const stamp = () => Math.floor(Date.now() / 1000);
const timezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch(e) {
        return 'UTC';
    }
};
/* The legacy panels, split out of the bundle every visitor downloads.
   managers/Components.js and views/Main are the entry points to the original
   application, and through them come @material-ui/core, lottie-web,
   velocity-react, react-draggable, google-libphonenumber, react-phone-number-input
   and moment-timezone. None of it is reachable from a marketing page, and all of
   it was being shipped to somebody who clicked an advertisement and landed on an
   article — before a single word rendered, because these pages draw themselves
   in the browser.
   lazy() rather than a rewrite: the panels are unchanged, they simply arrive
   when something routes to one. The cost is a moment's wait on first entry to
   the legacy app, paid by a signed-in user rather than by paid traffic. */
const legacy = () => import('managers/Components.js');
const About = lazy(() => legacy().then(m => ({ default: m.About })));
const Account = lazy(() => legacy().then(m => ({ default: m.Account })));
const DuraCode = lazy(() => legacy().then(m => ({ default: m.DuraCode })));
const DuraLink = lazy(() => legacy().then(m => ({ default: m.DuraLink })));
const Payments = lazy(() => legacy().then(m => ({ default: m.Payments })));
const Projects = lazy(() => legacy().then(m => ({ default: m.Projects })));
/* Privacy is bound to a channel at module scope rather than lazily, because the
   binding has to survive re-renders. Wrapping the bound component keeps that
   identity stable while still deferring the import. */
const PrivacyDuraLink = lazy(() => legacy().then(m => ({
    default: props => { const P = m.Privacy.bind(this, { channel: 'duralink' }); return <P {...props} />; }
})));
/* views/Main exports an object with an entry() method, not a component, so it
   cannot be lazy()'d directly — lazy expects something React can render. Wrapped
   as a component that calls entry() with its props, it defers the same way.

   Worth deferring: it reaches lottie-web through views/Lottie, and Utils through
   its own import, which is the pair of heavyweights again. It is only ever
   rendered for a signed-in user, and nobody arriving from an advertisement is
   signed in. */
/* Alerts, deferred for the same reason as the panels.

   views/Alert reaches velocity-react directly and, through views/TextField,
   google-libphonenumber, react-phone-number-input and moment-timezone. An alert
   is by definition not on screen when a page loads, so none of that belongs in
   the first download.

   The cost is a few hundred milliseconds before the first alert of a session
   appears. Worth naming rather than hiding: if an alert ever needs to be
   instant — a destructive confirmation, say — that one should not come through
   here. Today they are all after-the-fact notices. */
const Alert = lazy(() => import('views/Alert.js'));

/* Same again. DatePicker reaches files/Utils, and Utils is where
   moment-timezone and google-libphonenumber come from. It only ever renders
   when something has asked for a date, which is never on arrival. */
const DatePicker = lazy(() => import('views/DatePicker.js'));
const AlertStack = lazy(() => import('views/AlertStack.js'));

const AccountEntry = lazy(() => import('views/Main').then(m => ({
    default: props => m.default.entry(props)
})));

const ContentManager = Content.new();
const EventsManager = Events.new();

/* Views that paint their own page. They bring their own navigation, footer and
   ground colour, so the legacy shell's header and version strip stay out of
   their way. Add a new marketing view here or it will render inside the old
   chrome. */
const MARKETING_VIEWS = ['article', 'articles', 'contact', 'deep-look', 'deep-look-report', 'home', 'how-we-work', 'privacy', 'quick-look', 'terms', 'ways-to-work'];

/* The one place that decides what the address bar means. Used on first load and
   again whenever the back button moves through history, so both paths agree. */
const panels = {
    about: [{
        id: 'about',
        Component: About
    }],
    article: [{
        id: 'article',
        Component: Article
    }],
    articles: [{
        id: 'articles',
        Component: Articles
    }],
    account: [{
        id: 'account',
        Component: Account
    }],
    /* The marketing contact page, not the old one from managers/Components.js.
       The legacy app's own nav still points at `contact`, and now lands here
       too, which is the intent: there is one place to write to us. */
    contact: [{
        id: 'contact',
        Component: Contact
    }],
    duracode: [{
        id: 'duracode',
        Component: DuraCode
    }],
    duralink: [{
        id: 'duralink',
        Component: DuraLink
    }],
    duralink_privacy: [{
        id: 'privacy',
        Component: PrivacyDuraLink
    }],
    /* No `quick-look` entry. The Quick Look is an overlay rather than a page, so
       the view it was opened from stays on screen and dimmed behind it. */
    home: [{
        id: 'home',
        Component: HomePage
    }],
    /* The document pages are one component with a different markdown file
       behind each — see DOCS in views/DocPage.js. The wrappers are declared here
       at module scope so their identity is stable across renders. */
    /* Its own view, not a document. See the note at the top of DeepLook.js for
       why: an offer page and a three-thousand-word report rendered through the
       same template are indistinguishable, and neither one does its job. */
    'deep-look': [{
        id: 'deep-look',
        Component: DeepLook
    }],
    'deep-look-report': [{
        id: 'deep-look-report',
        Component: props => <DocPage doc={'deep-look-report'} {...props} />
    }],
    'how-we-work': [{
        id: 'how-we-work',
        Component: props => <DocPage doc={'how-we-work'} {...props} />
    }],
    /* The privacy policy and terms: documents, without a call to action. The
       panel id isn't 'privacy', which the legacy DuraLink policy still uses. */
    privacy: [{
        id: 'privacy-policy',
        Component: props => <DocPage doc={'privacy'} {...props} />
    }],
    terms: [{
        id: 'terms',
        Component: props => <DocPage doc={'terms'} {...props} />
    }],
    'ways-to-work': [{
        id: 'ways-to-work',
        Component: props => <DocPage doc={'ways-to-work'} {...props} />
    }],
    payments: [{
        id: 'payments',
        Component: Payments
    }],
    projects: [{
        id: 'projects',
        Component: Projects
    }],
    services: []
}

const App = () => {

    const accountElementRef = useRef(null);
    const layersRef = useRef(null);
    const userRef = useRef(null);

    const [active, setActive] = useState({ view: null });

    // the view the Quick Look was opened over, so closing it returns there
    const backgroundRef = useRef({ view: 'home' });

    // the last address a view was recorded for, so each one carries its
    // predecessor and the visit reads as a path — see the history effect below
    const trackedRef = useRef(null);
    const previousViewRef = useRef(null);
    const [alerts, setAlerts] = useState([]);
    const [badgeAnimations, setBadgeAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 26 },
        opacity: 0,
        transform: 'scale(0.5)'
    }));
    const [contentAnimations, setContentAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 1,
        top: 0,
    }));
    const [datePicker, setDatePicker] = useState(null);
    const [layers, setLayers] = useState([]);
    const [layerIndex, setLayerIndex] = useState([]);
    const [loader, setLoader] = useState(null);
    const [logoAnimations, setLogoAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 26 },
        left: -50,
        opacity: 0
    }));
    const [menuAnimations, setMenuAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 26 },
        right: -50,
        opacity: 0
    }));
    const [menuDrawerContentAnimations, setMenuDrawerContentAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 18 },
        left: 0
    }));
    const [menuDrawerAnimations, setMenuDrawerAnimations] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 18 },
        left: window.innerWidth
    }));
    const [menuExpanded, setMenuExpanded] = useState(false);
    const [selectedNavigationItem, setSelectedNavigationItem] = useState(false);
    const [nonce, setNonce] = useState(stamp());
    const [sheet, setSheet] = useState(null);
    const [size, setSize] = useState({ height: window.innerHeight, width: window.innerWidth });
    const [theme, setTheme] = useState('light');
    const [user, setUser] = useState(null);

    const onAccountClick = () => {
        utils.sheet.show({
            items: getAccountItems().concat([{
                key: 'cancel',
                title: 'Close',
                style: 'cancel'
            }]),
            position: 'bottom',
            sort: false,
            target: accountElementRef.current
        }, onParseAccountClick);
    }
    
    const onCloseLayer = layerID => {

        // filter layer id out of layer indexes
        setLayerIndex(layerIndex => {
            return update(layerIndex, {
                $apply: ids => ids.filter(id => id !== layerID)
            });
        });

        setLayers(layers => {

            // update layer and set visible flag to false
            let next = update(layers, {
                $apply: layers => layers.map(layer => {
                    if(layer.id === layerID) {
                        layer.visible = false;
                    }
                    return layer;
                })
            });

            // determine if at least one layer is still visible
            let targets = next.filter(layer => layer.visible !== false);
            if(targets.length === 0) {
                console.log('layers reset');
                document.body.style.overflowY = 'scroll';
                return [];
            }
            return next;
        });
    }

    const onLayerReposition = ({ id, position }) => {

        let index = layers.findIndex(layer => id === layer.id);
        if(index < 0) {
            console.log('no layer index');
            return;
        }
        setLayers(layers => {
            return update(layers, {
                [index]: {
                    position: {
                        $set: position
                    }
                }
            });
        });
    }

    const onLogin = (user, options = {}) => {

        // write login token to local storage
        localStorage.setItem('user.refresh_token', user.refresh_token);

        // update local state with user object
        userRef.current = user;
        setUser(user);

        // determine if a redirect was requested
        if(options.redirect) {
            setActive(options.redirect);
        }
    }

    /* Awaited rather than lazy(), because Login is one of the two legacy panels
       written as (bound, props) instead of (props). The .bind below is what
       supplies the first of those, and lazy() returns an object with no .bind on
       it — wrapping it would quietly change which argument onLogin arrives in.

       Importing at the click keeps the chunk out of the initial bundle just as
       effectively, and the panel is opening in response to a click anyway, so
       there is nothing to wait on that was not already waiting. */
    const onLoginClick = async () => {

        // hide navigation menu for mobile devices
        if(isMobile() === true) {
            setMenuExpanded(false);
            setSelectedNavigationItem(null);
        }

        const { Login } = await legacy();

        // open account signup layer
        utils.layer.open({
            Component: Login.bind(this, { onLogin }),
            id: 'login'
        });
    }

    const onLoginWithRefreshToken = async token => {
        try {

            // submit login request to server
            let { user } = await Request.post(utils, '/users/', {
                refresh_token: token,
                type: 'login'
            });
            onLogin(user);

        } catch(e) {
            console.error(e.message);
        }
    }

    const onMouseEnterMainContent = () => {

        // deselect currently selected navigation item some menu will hide
        setSelectedNavigationItem(false);
    }

    const onOpenLayer = nextLayer => {

        if(layers.find(layer => {
            return layer.id === nextLayer.id && layer.visible !== false
        })) {
            utils.alert.show({
                title: nextLayer.abstract ? nextLayer.abstract.getTitle() : 'Just a Second',
                message: `There is already a window open for ${nextLayer.abstract ? `"${nextLayer.abstract.getTitle()}"` : 'this information'}`
            });
            return;
        }

        setTimeout(() => {
            setLayers(layers => update(layers, {
                $push: [nextLayer]
            }))
            setLayerIndex(layerIndex => update(layerIndex, {
                $unshift: [nextLayer.id]
            }))
        }, 0)
    }

    const onParseAccountClick = key => {
        switch(key) {
            case 'account':
            break;

            case 'payments':
            case 'projects':
            setActive({ view: key });
            break;

            case 'logout':
            onRequestLogout();
            break;
        }
    }

    const onRequestLogout = () => {
        utils.alert.show({
            title: 'Logout',
            message: 'Are you sure that you want to logout of your account?',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    localStorage.removeItem('user.refresh_token');
                    setActive({ view: 'home' });
                    setUser(null);
                    return;
                }
            }
        });
    }

    const onSelectedNavigationItemChange = () => {
        if(selectedNavigationItem === null) {
            setMenuDrawerContentAnimations({ left: 0 });
            return;
        }
        if(menuExpanded === true) {
            setMenuDrawerContentAnimations({ left: -size.width });
        }
        if(menuExpanded === false) {
            setSelectedNavigationItem(null);
        }
    }

    const onSetLayerIndex = layerID => {

        let index = layers.findIndex(layer => layer.id === layerID);
        if(index < 0) {
            console.log('no layer index');
            return;
        }
        setLayers(layers, update(layers, {
            $apply: layers => layers.map(layer => {
                layer.moveToFront = layer.id === layerID;
                return layer;
            })
        }))

        let _index = layerIndex.findIndex(id => id === layerID);
        setLayerIndex(layerIndex, update(layerIndex, {
            $splice: [
                [_index, 1],
                [0, 0, layerID]
            ]
        }))
    }

    const onSetStyleSheetProperties = () => {
        setTheme(window.theme);
        document.body.className = window.theme;
        document.documentElement.style.setProperty('--theme', window.theme);
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());
    }

    const onSizeChange = () => {
        setSize({ height: window.innerHeight, width: window.innerWidth });
    }

    const onToggleMobileMenu = () => {
        setMenuExpanded(val => !val);
        setSelectedNavigationItem(null);
    }

    const onUpdateTheme = evt => {
        window.theme = 'dark'; //evt.matches ? 'dark' : 'light';
        onSetStyleSheetProperties();
        setNonce(stamp());
    }

    const getAccountComponents = () => {
        if(user) {
            return (
                <div 
                ref={accountElementRef}
                style={{
                    ...Appearance.styles.unstyledPanel(),
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'left'
                }}>
                    <AccountEntry
                    bottomBorder={false}
                    icon={{ path: 'images/user-placeholder.png' }}
                    onClick={onAccountClick}
                    style={{ padding: '8px 14px 8px 14px' }}
                    subTitle={user.email_address}
                    title={user.full_name} />
                </div>
            )
        }
        /*
        return (
            <span 
            className={'text-button'}
            onClick={onLoginClick}
            style={{
                ...Appearance.textStyles.title(),
                marginRight: 32
            }}>{'Login'}</span>
        )
        */
    }

    const getAccountItems = () => {
        return [{
            key: 'payments',
            title: 'Payments',
            style: 'default'
        },{
            key: 'projects',
            title: 'Projects',
            style: 'default'
        },{
            key: 'settings',
            title: 'Settings',
            style: 'default'
        },{
            key: 'logout',
            title: 'Logout',
            style: 'destructive'
        }].map(item => ({
            ...item,
            onClick: () => {
                onParseAccountClick(item.key)
                if(isMobile() === true) {
                    setMenuExpanded(false);
                }
            }
        }));
    }

    const getApps = () => {
        return [{
            icon: 'images/duracode-app-icon.jpg',
            key: 'duracode',
            title: 'DuraCode'
        },{
            icon: 'images/duralink-app-icon.jpg',
            key: 'duralink',
            title: 'DuraLink'
        }];
    }

    const getAppNavigationComponents = () => {
        let components = getApps().map((app, index, apps) => {
            return (
                <div 
                className={'text-button'}
                key={index}
                onClick={setActive.bind(this, { view: app.key })}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== apps.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                    display: 'flex',
                    flexDirection: 'row',
                    padding: 12,
                    width: '100%'
                }}>
                    <img 
                    src={app.icon}
                    style={{
                        borderRadius: 8,
                        height: 30,
                        marginRight: 12,
                        width: 30
                    }} />
                    <span style={{
                        ...Appearance.textStyles.title(),
                        flexGrow: 1,
                        paddingRight: 36
                    }}>{app.title}</span>
                    <img
                    src={'images/next-arrow-grey-small.png'}
                    style={{
                        width: 12,
                        height: 12,
                        objectFit: 'contain',
                        marginLeft: 8,
                        opacity: 0.75
                    }} />
                </div>
            )  
        });
        return getNavigationContainer(components);
    }

    const getDesktopVersionDetails = () => {
        return isMobile() === false && (
            <div className={'dura-reveal'}>
                <div style={{
                    background: '#0A0A0B',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    padding: '32px 32px 40px',
                    width: '100%'
                }}>
                    <div style={{
                        maxWidth: 1440,
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 16
                    }}>
                        <span className={'dura-mono'} style={{
                            color: 'rgba(245,245,244,0.45)',
                            fontSize: 11,
                            fontWeight: 500
                        }}>© {new Date().getFullYear()} Dura Labs LLC · Made with care</span>
                        <span className={'dura-mono'} style={{
                            color: 'rgba(245,245,244,0.35)',
                            fontSize: 11,
                            fontWeight: 500
                        }}>v{API.app_version} · build {API.build}</span>
                    </div>
                </div>
            </div>
        )
    }

    const getLayers = () => {
        return layers.map(({ id, abstract, options, title, visible, Component }, index) => {

            if(visible === false) {
                // must return null instead of using filter
                // using filter for visible does not preseve other visible layers
                return null;
            }
            return (
                <Component
                key={index}
                title={title}
                index={index}
                utils={utils}
                abstract={abstract}
                options={{
                    ...options,
                    index: index,
                    onClose: onCloseLayer,
                    onReposition: onLayerReposition,
                    onSetLayerIndex: onSetLayerIndex,
                    zIndex: EndIndex - layerIndex.findIndex(indexID => id === indexID)
                }}/>
            )
        });
    }

    const getHeader = () => {

        // prepare naivgation items
        let items = [{
            key: 'home',
            onClick: setActive.bind(this, { view: 'home' }),
            title: 'Home'
        },{
            key: 'contact',
            onClick: setActive.bind(this, { view: 'contact' }),
            title: 'Contact'
        },{
            key: 'about',
            onClick: setActive.bind(this, { view: 'about' }),
            title: 'About Us'
        },{
            key: 'account',
            title: 'My Account'
        }];

        // determine if current device is a mobile device
        if(isMobile() === true) {
            return (
                <>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: 'rgba(10,10,11,0.7)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    borderBottom: `1px solid rgba(255,255,255,0.06)`,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: 0,
                    padding: '16px 20px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <animated.div 
                    className={'text-button'}
                    onClick={setActive.bind(this, { view: 'home' })}
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        paddingLeft: 8,
                        position: 'relative',
                        ...logoAnimations
                    }}>
                        <img 
                        src={theme === 'dark' ? '/images/logo-text-white.png' : '/images/logo-text-grey.png'}
                        style={{
                            height: 20,
                            width: 'auto'
                        }} />
                    </animated.div>
                    <animated.img 
                    className={'text-button'}
                    onClick={onToggleMobileMenu}
                    src={`images/navigation-icon-${theme}.png`}
                    style={{
                        height: 30,
                        objectFit: 'contain',
                        position: 'relative',
                        width: 30,
                        ...menuAnimations
                    }} />
                </div>
                <animated.div style={{
                    backgroundColor: Appearance.colors.layerBackground(),
                    bottom: 0,
                    height: '100%',
                    overflow: 'hidden',
                    position: 'fixed',
                    top: 0,
                    width: '100%',
                    zIndex: 5500,
                    ...menuDrawerAnimations
                }}>
                    <animated.div style={{
                         alignItems: 'center',
                         display: 'flex',
                         flexDirection: 'row',
                         height: '100dvh',
                         overflow: 'hidden',
                         position: 'relative',
                         width: size.width * 2,
                         ...menuDrawerContentAnimations
                    }}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-around',
                            height: '100%',
                            padding: `128px 20px 148px 20px`,
                            width: size.width
                        }}>
                            {items.map((item, index) => {
                                /*
                                if(!user && item.key === 'account') {
                                    return (
                                        <span 
                                        className={'text-button'}
                                        key={index}
                                        onClick={onLoginClick}
                                        style={{
                                            ...Appearance.textStyles.layerItemTitle(),
                                            fontSize: 24
                                        }}>{'Login'}</span>
                                    )
                                }
                                */
                                return (
                                    <span 
                                    className={'text-button'}
                                    key={index}
                                    onClick={item.onClick || setSelectedNavigationItem.bind(this, item.key)}
                                    style={{
                                        ...Appearance.textStyles.layerItemTitle(),
                                        fontSize: 24,
                                        ...item.key === active.view && {
                                            color: Appearance.colors.primary()
                                        }
                                    }}>{item.title}</span>
                                )
                            })}
                        </div>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-around',
                            height: '100%',
                            padding: `128px 20px 148px 20px`,
                            position: 'relative',
                            width: size.width
                        }}>
                            {getInnerDrawerComponents()}
                            <img
                            className={'text-button'}
                            onClick={setSelectedNavigationItem.bind(this, null)}
                            src={`images/back-arrow-${theme}.png`}
                            style={{
                                height: 25,
                                objectFit: 'contain',
                                position: 'absolute',
                                left: 20,
                                top: 20,
                                width: 18
                            }} />
                        </div>
                    </animated.div>
                    <img
                    className={'text-button'}
                    onClick={onToggleMobileMenu}
                    src={`images/close-button-${theme}.png`}
                    style={{
                        height: 25,
                        objectFit: 'contain',
                        position: 'absolute',
                        right: 20,
                        top: 20,
                        width: 25
                    }} />
                    <div style={{
                        bottom: 0,
                        padding: 24,
                        position: 'absolute',
                        textAlign: 'center',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            display: 'block'
                        }}>{`Version ${API.app_version} Build ${API.build}`}</span>
                    </div>
                </animated.div>
                </>
            )
        }
        
        // fallback to rendering content for tablets and desktop
        return (
            <div style={{
                alignItems: 'center',
                backgroundColor: 'rgba(10,10,11,0.65)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderBottom: `1px solid rgba(255,255,255,0.06)`,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 0,
                padding: '14px 32px',
                position: 'sticky',
                top: 0,
                zIndex: 100,
                textAlign: 'center',
                width: '100vw'
            }}>
                <animated.div 
                className={'text-button'}
                onClick={setActive.bind(this, { view: 'home' })}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    paddingLeft: 8,
                    position: 'relative',
                    width: 'calc(50vw - 50px)',
                    ...logoAnimations
                }}>
                    <img 
                    src={theme === 'dark' ? '/images/logo-text-white.png' : '/images/logo-text-grey.png'}
                    style={{
                        height: 25,
                        width: 'auto'
                    }} />
                </animated.div>
                <animated.img 
                className={'text-button'}
                onClick={setActive.bind(this, { view: 'home' })}
                src={'/images/logo.png'}
                style={{
                    height: 40,
                    objectFit: 'contain',
                    width: 40,
                    ...badgeAnimations,
                    ...size.width < 1300 && {
                        opacity: 0
                    }
                }} />
                <animated.div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    position: 'relative',
                    width: 'calc(50vw - 50px)',
                    ...menuAnimations
                }}>
                    {items.map((item, index) => {
                        if(item.key === 'account') {
                            return (
                                <div 
                                key={index}
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative'
                                }}>
                                    {getAccountComponents()}
                                </div>
                            )
                        }
                        return (
                            <div
                            key={index}
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                marginRight: 28,
                                position: 'relative'
                            }}>
                                <span
                                className={`dura-nav-link ${item.key === active.view ? 'active' : ''}`}
                                onClick={item.onClick}
                                onMouseEnter={setSelectedNavigationItem.bind(this, item.key)}>{item.title}</span>
                                {selectedNavigationItem === item.key && getNavigationComponents(item.key)}
                            </div>
                        )
                    })}
                </animated.div>
            </div>
        )
    }

    const getInnerDrawerComponents = () => {
        switch(selectedNavigationItem) {
            case 'apps':
            return getApps().map((app, index) => {
                return (
                    <span 
                    className={'text-button'}
                    key={index}
                    onClick={setActive.bind(this, { view: app.key })}
                    style={{
                        ...Appearance.textStyles.layerItemTitle(),
                        fontSize: 24
                    }}>{app.title}</span>
                )
            });

            case 'account':
            return getAccountItems().map((entry, index) => {
                return (
                    <span 
                    className={'text-button'}
                    key={index}
                    onClick={entry.onClick || setActive.bind(this, { view: entry.key })}
                    style={{
                        ...Appearance.textStyles.layerItemTitle(),
                        fontSize: 24
                    }}>{entry.title}</span>
                )
            });

            default:
            return null;
        }
    }

    /* The Quick Look is a modal over whatever the owner was reading, so the page
       underneath keeps rendering while it is open. Landing on ?route=quick-look
       directly has nothing behind it, so it falls back to the home page. */
    const getMainContent = () => {

        let beneath = active.view === 'quick-look' ? backgroundRef.current : active;
        let targets = panels[beneath.view] || [];
        return (
            <animated.div style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                width: '100%',
                ...contentAnimations
            }}>
                {!MARKETING_VIEWS.includes(beneath.view) && getHeader()}
                {getLayers()}
                <div 
                onMouseEnter={onMouseEnterMainContent}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    width: '100%'
                }}>
                    <div
                    style={{
                        position: 'relative',
                        width: '100%'
                    }}>
                        {targets.map(({ Component }, index) => (
                            <Component
                            active={beneath}
                            index={index}
                            key={index}
                            utils={utils}/>
                        ))}
                    </div>
                </div>
            </animated.div>
        )
    }

    const getNavigationComponents = () => {
        switch(selectedNavigationItem) {
            case 'apps':
            return getAppNavigationComponents();

            default:
            return null;
        }
    }

    const getNavigationContainer = (components, lastItem) => {
        return (
            <div 
            onMouseLeave={setSelectedNavigationItem.bind(this, false)}
            style={{
                alignItems: lastItem ? 'flex-end' : 'center',
                display: 'flex',
                flexDirection: 'column',
                position: 'absolute',
                top: 40,
                right: lastItem ? 0 : null,
                zIndex: 7500
            }}>
                <img 
                src={theme === 'dark' ? 'images/floating-menu-carrot-grey.png' : 'images/floating-menu-carrot-white.png'}
                style={{
                    height: 25,
                    objectFit: 'contain',
                    position: 'relative',
                    top: 8,
                    right: lastItem ? 12 : null,
                    width: 25
                }} />
                <div style={{
                    background: Appearance.colors.layerBackground(),
                    border: `1px solid ${Appearance.colors.divider()}`,
                    borderRadius: 12,
                    boxShadow: '5px 5px 20px rgba(0,0,0,0.05)',
                    textAlign: 'left'
                }}>
                    {components}
                </div>
            </div>
        )
    }

    const setupStructure = () => {

        // set default active view
        setActive(routeFromLocation());

        // amimate components into view, lol
        setBadgeAnimations({ opacity: 1, transform: 'scale(1)' });
        setContentAnimations({ opacity: 1, top: 0 });
        setLogoAnimations({ left: 0, opacity: 1 });
        setMenuAnimations({ opacity: 1, right: 0 });

        // attempt to run auto login using auth token
        let token = localStorage.getItem('user.refresh_token');
        if(token) {
            onLoginWithRefreshToken(token);
        }
    }

    const utils = {
        alert: {
            show: async props => {
                try {
                    if(loader) {
                        await utils.loader.hide();
                    }
                    setAlerts(alerts => update(alerts, {
                        $push: [{
                            id: `${stamp()}-${Math.random()}`,
                            ...props
                        }]
                    }))
                } catch(e) {
                    console.log(e.message)
                }
            },
            dev: async () => {
                try {
                    if(loader) {
                        await utils.loader.hide();
                    }
                    setAlerts(alerts => update(alerts, {
                        $push: [{
                            id: `${stamp()}-${Math.random()}`,
                            title: 'In Development',
                            message: 'This feature is currently under development and will become available at a later date'
                        }]
                    }))

                } catch(e) {
                    console.log(e.message)
                }
            },
        },
        animations: {
            get: key => {
                switch(key) {
                    case 'panel':
                    return {
                        animateIn: {
                            top: 0,
                            opacity: 1
                        },
                        animateOut: {
                            top: -50,
                            opacity: 0
                        },
                        config: {
                            friction: 10
                        },
                        delay: 0.5
                    }

                    default:
                    return null;
                }
            }
        },
        api: {
            headers: () => {
                return {
                    'Content-Type': 'application/json',
                    'X-API': `Version ${API.version}`,
                    'X-Timezone': `TZ ${timezone()}`,
                    'X-Web': `Build ${API.build}`,
                    ...userRef.current && {
                        'Authorization': `Bearer ${userRef.current.token}`,
                        'Identification': `User ${userRef.current.user_id}`
                    }
                }
            }
        },
        content: ContentManager,
        datePicker: {
            show: props => setDatePicker({
                utils: utils,
                id: `${stamp()}-${Math.random()}`,
                ...props
            })
        },
        events: EventsManager,
        layer: {
            open: layer => onOpenLayer(layer),
            close: layer => onCloseLayer(layer),
            requestClose: id => {
                let closeEvent = new CustomEvent('onLayerAction', {
                    detail: {
                        action: 'close',
                        layerID: id
                    }
                });
                window.dispatchEvent(closeEvent);
            }
        },
        loader: {
            show: async () => {
                return new Promise(resolve => {
                    setLoader(true);
                    setTimeout(resolve, 500)
                });
            },
            hide: async () => {
                return new Promise(resolve => {
                    setLoader(false);
                    setTimeout(resolve, 500)
                });
            }
        },
        navigation: {
            set: setActive
        },
        sheet: {
            show: (sheet, callback) => {
                setSheet({ ...sheet, onClick: callback })
            }
        },
        user: {
            set: onLogin
        }
    }

    useEffect(() => {
        // opening or closing the Quick Look leaves the page behind it where it
        // was; only a real change of page starts at the top
        const crossing = active.view === 'quick-look' || previousViewRef.current === 'quick-look';
        previousViewRef.current = active.view;
        if(!crossing) {
            window.scrollTo(0,0);
        }
        if(isMobile() === true) {
            setMenuExpanded(false);
        }
    }, [active]);

    /* Marketing views own the address bar, so an article can be linked, shared,
       refreshed and reached with the back button. The legacy views are left
       alone — they have never had URLs and giving them one here would change
       behaviour nobody asked for. */
    useEffect(() => {
        if(!MARKETING_VIEWS.includes(active.view)) {
            return;
        }
        /* One address per page, always.

           An arrival through the old ?route= form is rewritten rather than
           pushed, so it is corrected in place without leaving a history entry
           that the back button would bounce off. Anything else is a navigation
           and gets a real entry. Without the distinction a search engine sees
           two urls for every page and has to decide which is the real one,
           which it does by guessing. */
        const target = locationForRoute(active);
        const current = `${window.location.pathname}${window.location.search}`;

        if(current !== target) {

            /* Replaced, not pushed, when the address is being corrected rather
               than navigated.

               Two shapes of that. The old ?route= form on the root, which is
               where this started. And a real path arriving with a query string
               on it — a campaign tag, or the tracking on the link in a Quick
               Look receipt. Both are the same address as the target with
               something extra, and pushing them leaves an entry the back button
               bounces off: press back and you land on the tracked url, which
               instantly corrects itself forward again. */
            const samePath = window.location.pathname === target.split('?')[0];
            const correction = Boolean(window.location.search)
                && (window.location.pathname === '/' || samePath);

            window.history[correction ? 'replaceState' : 'pushState']({}, '', target);
        }

        /* A view is recorded here, after the address has been settled, because
           this is the one place in the app that decides a navigation happened.
           Letting the tracker watch the history api instead would hand that
           judgement to a script that cannot tell a page from a modal opening
           over one.

           `trackedRef` holds the previous address so each view carries where it
           came from. Inside a single page app document.referrer never moves off
           whatever sent the visitor here in the first place, so without this
           every internal step would look like a fresh arrival and there would be
           no path to read afterwards. See files/analytics.js. */
        if(trackedRef.current !== target) {
            trackView(target, trackedRef.current);
            trackedRef.current = target;
        }

    }, [active]);

    useEffect(() => {
        const onPopState = () => setActive(routeFromLocation());
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // loaded once, after the first render rather than during it, so a slow or
    // blocked tracker can never sit in front of the page it is measuring
    /* The campaign is not read here. It is captured when analytics.js is
       imported, because the history effect above rewrites the address — and
       the query string with it — before any effect declared down here runs. */
    useEffect(() => { startAnalytics(); }, []);

    /* Remember where the Quick Look was opened from, and stop the page behind it
       from scrolling while it is open — otherwise a wheel gesture over the dim
       ground moves the page underneath the modal. */
    useEffect(() => {

        if(!active.view) {
            return;
        }
        if(active.view !== 'quick-look') {
            backgroundRef.current = active;
            return;
        }
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; }

    }, [active]);

    useEffect(() => {
        layersRef.current = layers;
    }, [layers]);

    useEffect(() => {
        if(isMobile() === false) {
            setMenuDrawerAnimations({ left: size.width });
            return;
        }
        setMenuDrawerAnimations({ left: menuExpanded ? 0 : size.width });
    }, [menuExpanded, size]);

    useEffect(() => {
        if(isMobile() === true) {
            onSelectedNavigationItemChange();
        }
    }, [selectedNavigationItem]);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    useEffect(() => {

        setupStructure();

        // setup window listeners
        window.addEventListener('resize', onSizeChange);
        window.addEventListener('beforeunload', evt => {
            evt.returnValue = null;
            return null;
        });

        // css variables
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());

        // theme and theme listeners
        window.theme = 'dark'; //window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        document.documentElement.style.setProperty('--theme', window.theme);
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', onUpdateTheme);
        onSetStyleSheetProperties();

        // scroll-triggered reveal: any element with .dura-reveal class
        // gains .is-visible when it enters the viewport. one-shot, then unobserved.
        const revealObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if(entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    revealObserver.unobserve(entry.target);
                }
            });
        }, { threshold: 0.05, rootMargin: '0px 0px -8% 0px' });

        const observeAll = root => {
            root.querySelectorAll('.dura-reveal:not(.is-visible)').forEach(el => {
                revealObserver.observe(el);
            });
        };
        observeAll(document.body);

        const mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(m => {
                m.addedNodes.forEach(node => {
                    if(node.nodeType !== 1) {
                        return;
                    }
                    if(node.classList && node.classList.contains('dura-reveal') && !node.classList.contains('is-visible')) {
                        revealObserver.observe(node);
                    }
                    if(node.querySelectorAll) {
                        observeAll(node);
                    }
                });
            });
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

    }, []);

    return (
        <div
        className={`root-container ${theme}`}
        nonce={nonce}
        style={{
            height: size.height,
            position: 'relative',
            width: size.width
        }}>
            {/* One boundary around everything, because a lazy component throws
                while its chunk is in flight and React needs somewhere to catch
                it. The fallback is deliberately nothing: the marketing pages are
                never behind it, and a signed-in user waiting a few hundred
                milliseconds for a panel is better served by the page not
                flickering a spinner into the space it is about to fill. */}
            <Suspense fallback={null}>
            {getMainContent()}
            {active.view === 'quick-look' && (
                <QuickLook
                intent={active.intent}
                onClose={() => setActive(backgroundRef.current)}
                shared={active.read}
                utils={utils} />
            )}
            {!MARKETING_VIEWS.includes(active.view) && getDesktopVersionDetails()}
            {datePicker && (
                <DatePicker
                {...datePicker}
                onClose={() => {
                    setDatePicker(null);
                    if(typeof(datePicker.onClose) === 'function') {
                        datePicker.onClose();
                    }
                }} />
            )}
            {sheet && (
                <Sheet
                {...sheet}
                onClose={setSheet.bind(this, null)}/>
            )}
            <AlertStack>
                {alerts.map((alert, index) => (
                    <Alert
                    {...alert}
                    key={index}
                    index={(alerts.length - 1) - index}
                    onClose={id => {
                        if(typeof(alert.onClose) === 'function') {
                            alert.onClose();
                        }
                        setAlerts(alerts => {
                            return alerts.filter(alert => {
                                return id !== alert.id;
                            });
                        })
                    }} 
                    utils={utils} />
                ))}
            </AlertStack>
            </Suspense>
        </div>
    )
}

export default App;
